import { StringUtility } from './string';
import { RegularExpression } from '../core/regular_expression';
import { CurriedFunction1, CurriedFunction2, CurriedFunction3, CurriedFunction4 } from 'lodash4';
import * as _4 from 'lodash4';

export interface ITints {
  tint_10: Array<number> | string;
  tint_20: Array<number> | string;
  tint_30: Array<number> | string;
  tint_40: Array<number> | string;
  tint_50: Array<number> | string;
  tint_60: Array<number> | string;
  tint_70: Array<number> | string;
  tint_80: Array<number> | string;
  tint_90: Array<number> | string;
}

export interface IShades {
  shade_10: Array<number> | string;
  shade_20: Array<number> | string;
  shade_30: Array<number> | string;
  shade_40: Array<number> | string;
  shade_50: Array<number> | string;
  shade_60: Array<number> | string;
  shade_70: Array<number> | string;
  shade_80: Array<number> | string;
  shade_90: Array<number> | string;
}

interface IColorSet {
  _10: IColorRGB;
  _20: IColorRGB;
  _30: IColorRGB;
  _40: IColorRGB;
  _50: IColorRGB;
  _60: IColorRGB;
  _70: IColorRGB;
  _80: IColorRGB;
  _90: IColorRGB;
}

enum ColorRGBType {
  R,
  G,
  B
}

enum ColorTransformation {
  shade = -1,
  tint = 1
}

/**
 * 0 <= c <= 255
 */
type rgb_color = number;

/**
 * A color object for red, green, blue each between 0 and 255
 */
export interface IColorRGB {
  r: rgb_color;
  g: rgb_color;
  b: rgb_color;
}

export type color_rule = 'bkg' | 'txt' | 'bdr';

export class ColorUtility {
  static universityStyles: Record<string, string> = {};

  static mapWordToTertiaryColor(word: string, colorRule: color_rule = 'bkg'): string {
    return `clr-${(StringUtility.toNumericHash(word) % 12) + 1}-${colorRule}`;
  }

  /**
   * Convert three colors to a rgb color
   * @param r red (0, 255)
   * @param g green (0, 255)
   * @param b blue (0, 255)
   * @returns a color object for r, g, b
   */
  static buildColorRGB(r: rgb_color, g: rgb_color, b: rgb_color): IColorRGB {
    return {
      r: ColorUtility.__clipColor(r),
      g: ColorUtility.__clipColor(g),
      b: ColorUtility.__clipColor(b)
    };
  }

  private static __clipColor(color: rgb_color): rgb_color {
    return Math.max(0, Math.min(255, color));
  }

  /**
   * Convert a rgb color to a hexadecimal string
   */
  static rgbToHex: CurriedFunction1<IColorRGB, string> = _4.curry((colorRGB: IColorRGB): string => {
    const r: string = ColorUtility._decimalToHex(colorRGB.r);
    const g: string = ColorUtility._decimalToHex(colorRGB.g);
    const b: string = ColorUtility._decimalToHex(colorRGB.b);

    return `#${r}${g}${b}`;
  });

  private static _decimalToHex(decimal: number): string {
    const hex = decimal.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }

  /**
   * Convert a hexadecimal string to a rgb color
   */
  static hexToRgb: CurriedFunction1<string, IColorRGB> = _4.curry((colorHex: string): IColorRGB => {
    const color: string[] = RegularExpression.exec(RegularExpression.COLOR_HEX, colorHex).slice(1, 4);

    return ColorUtility.buildColorRGB(
      ColorUtility._hexToDecimal(color[ColorRGBType.R]),
      ColorUtility._hexToDecimal(color[ColorRGBType.G]),
      ColorUtility._hexToDecimal(color[ColorRGBType.B])
    );
  });

  private static _hexToDecimal(hex: string): number {
    return parseInt(hex, 16);
  }

  /**
   * Convert a css rgb string to a rgb color
   */
  static cssRgbToRgb: CurriedFunction1<string, IColorRGB> = _4.curry((colorCssRGB: string): IColorRGB => {
    const colorRGB: string[] = RegularExpression.split(RegularExpression.COLOR_CSS_RGBA, colorCssRGB).slice(1);

    return ColorUtility.buildColorRGB(
      parseInt(colorRGB[ColorRGBType.R], 10),
      parseInt(colorRGB[ColorRGBType.G], 10),
      parseInt(colorRGB[ColorRGBType.B], 10)
    );
  });

  /**
   * Convert a rgb array to a rgb color
   */
  static listRgbToRgb: CurriedFunction1<(string | number)[], IColorRGB> = _4.curry(
    (colorRawRGB: (string | rgb_color)[]): IColorRGB => {
      return ColorUtility.buildColorRGB(
        ColorUtility._colorToColorNumber(colorRawRGB[ColorRGBType.R]),
        ColorUtility._colorToColorNumber(colorRawRGB[ColorRGBType.R]),
        ColorUtility._colorToColorNumber(colorRawRGB[ColorRGBType.R])
      );
    }
  );

  private static _colorToColorNumber(value: string | rgb_color): rgb_color {
    return typeof value === 'number' && value === value ? value : parseInt(value as string, 10);
  }

  /**
   * Tint a rgb color by a percentage between -1 and 1
   * @param percent is negative or positive tint
   */
  static transformColor: CurriedFunction2<number, IColorRGB, IColorRGB> = _4.curry(
    (percent: number, colorRGB: IColorRGB): IColorRGB => {
      return ColorUtility.buildColorRGB(
        ColorUtility.__transformColorByPercentage(percent, colorRGB.r),
        ColorUtility.__transformColorByPercentage(percent, colorRGB.g),
        ColorUtility.__transformColorByPercentage(percent, colorRGB.b)
      );
    }
  );

  private static __transformColorByPercentage(percent: number, color: rgb_color): rgb_color {
    const _color = ColorUtility.__clipColor(color);

    return percent < 0 ? ColorUtility.__shadeColor(percent * -1, _color) : ColorUtility.__tintColor(percent, _color);
  }

  /**
   * @param percent is > 0
   * @param color (0 <= n <= 255)
   */
  private static __shadeColor(percent: number, color: rgb_color): rgb_color {
    return color * (1 - percent);
  }

  /**
   * @param percent is > 0
   */
  private static __tintColor(percent: number, color: rgb_color): rgb_color {
    return color + (255 - color) * Math.abs(percent);
  }

  /**
   * transform
   */
  static buildColorSet: CurriedFunction2<ColorTransformation, IColorRGB, IColorSet> = _4.curry(
    (colorTransformation: ColorTransformation, colorRGB: IColorRGB): IColorSet => {
      return {
        _10: ColorUtility.transformColor(colorTransformation * 0.1, colorRGB),
        _20: ColorUtility.transformColor(colorTransformation * 0.2, colorRGB),
        _30: ColorUtility.transformColor(colorTransformation * 0.3, colorRGB),
        _40: ColorUtility.transformColor(colorTransformation * 0.4, colorRGB),
        _50: ColorUtility.transformColor(colorTransformation * 0.5, colorRGB),
        _60: ColorUtility.transformColor(colorTransformation * 0.6, colorRGB),
        _70: ColorUtility.transformColor(colorTransformation * 0.7, colorRGB),
        _80: ColorUtility.transformColor(colorTransformation * 0.8, colorRGB),
        _90: ColorUtility.transformColor(colorTransformation * 0.9, colorRGB)
      };
    }
  );

  static buildShades: CurriedFunction1<IColorRGB, IColorSet> = ColorUtility.buildColorSet(ColorTransformation.shade);
  static buildTints: CurriedFunction1<IColorRGB, IColorSet> = ColorUtility.buildColorSet(ColorTransformation.tint);

  /**
   * Uses the background color to determine whether the text color should
   * be black or white. The formula used is W3C guidelines compliant.
   * https://www.w3.org/TR/WCAG20/#relativeluminancedef
   * @param bgHexColor background color
   */
  static getTextColorForBackgroundColor(bgHexColor: string) {
    const rgb = this.hexToRgb(bgHexColor);
    const backgroundUIColor = [rgb.r / 255, rgb.b / 255, rgb.g / 255];
    const W3CRelativeRGB = _4.map(backgroundUIColor, (color: number) => {
      return color <= 0.03928 ? color / 12.92 : Math.pow((color + 0.055) / 1.055, 2.4);
    });

    const relativeLuminance = 0.2126 * W3CRelativeRGB[0] + 0.7152 * W3CRelativeRGB[1] + 0.0722 * W3CRelativeRGB[2];
    return relativeLuminance > 0.179 ? 'black' : 'white';
  }

  // OLD COLOR UTILITY

  static hexRegex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

  static transformColor2 = (primaries: Array<string | number>, percent: number): Array<number> => {
    let baseNumber: number = percent < 0 ? 0 : 255; //if given a negative, make darker, positive makes lighter
    let percentChange: number = percent < 0 ? percent * -1 : percent;
    let R: number = parseInt(primaries[0] as string);
    let G: number = parseInt(primaries[1] as string);
    let B: number = parseInt(primaries[2] as string);

    const calculateNewValue = (primaryColor: number): number => {
      return Math.round((baseNumber - primaryColor) * percentChange) + primaryColor;
    };

    return [calculateNewValue(R), calculateNewValue(G), calculateNewValue(B)];
  };

  static decimalToHex = (decimal: number): string => {
    let hex = decimal.toString(16);
    if (hex.length === 1) {
      hex = '0' + hex;
    }
    return hex;
  };

  static hexToDecimal = (hex: string): number => {
    return parseInt(hex, 16);
  };

  static convertHexToRgb(hexValue: string): Array<number> {
    let color: Array<string> = this.hexRegex.exec(hexValue)!.slice(1, 4);
    return [this.hexToDecimal(color[0]), this.hexToDecimal(color[1]), this.hexToDecimal(color[2])];
  }

  static convertRgbToHex(rgbValue: Array<number>): string {
    return '#' + this.decimalToHex(rgbValue[0]) + this.decimalToHex(rgbValue[1]) + this.decimalToHex(rgbValue[2]) + '';
  }

  static transformHexToHex(hexValue: string, percent: number): string {
    return this.convertRgbToHex(this.transformColor2(this.convertHexToRgb(hexValue), percent));
  }

  static transformRgbToRgb(rgbValue: Array<number> | string, percent: number): Array<number> {
    if (_4.isString(rgbValue)) {
      rgbValue = (rgbValue + '').split(',') as Array<any>;
    }

    return this.transformColor2(rgbValue as Array<number>, percent);
  }

  static transformRgbToHex(rgbValue: Array<number> | string, percent: number): string {
    if (_4.isString(rgbValue)) {
      rgbValue = (rgbValue + '').split(',') as Array<any>;
    }

    return this.convertRgbToHex(this.transformColor2(rgbValue as Array<number>, percent));
  }

  static transformHexToRgb(hexValue: string, percent: number): Array<number> {
    return this.transformColor2(this.convertHexToRgb(hexValue), percent);
  }

  static getAllTintsOfHex(hexValue: string): ITints {
    return {
      tint_10: this.transformHexToHex(hexValue, 0.9),
      tint_20: this.transformHexToHex(hexValue, 0.8),
      tint_30: this.transformHexToHex(hexValue, 0.7),
      tint_40: this.transformHexToHex(hexValue, 0.6),
      tint_50: this.transformHexToHex(hexValue, 0.5),
      tint_60: this.transformHexToHex(hexValue, 0.4),
      tint_70: this.transformHexToHex(hexValue, 0.3),
      tint_80: this.transformHexToHex(hexValue, 0.2),
      tint_90: this.transformHexToHex(hexValue, 0.1)
    };
  }

  static getAllTintsOfRgb(rgbValue: Array<number>): ITints {
    return {
      tint_10: this.transformRgbToRgb(rgbValue, 0.9),
      tint_20: this.transformRgbToRgb(rgbValue, 0.8),
      tint_30: this.transformRgbToRgb(rgbValue, 0.7),
      tint_40: this.transformRgbToRgb(rgbValue, 0.6),
      tint_50: this.transformRgbToRgb(rgbValue, 0.5),
      tint_60: this.transformRgbToRgb(rgbValue, 0.4),
      tint_70: this.transformRgbToRgb(rgbValue, 0.3),
      tint_80: this.transformRgbToRgb(rgbValue, 0.2),
      tint_90: this.transformRgbToRgb(rgbValue, 0.1)
    };
  }

  static getAllShadesOfHex(hexValue: string): IShades {
    return {
      shade_10: this.transformHexToHex(hexValue, -0.1),
      shade_20: this.transformHexToHex(hexValue, -0.2),
      shade_30: this.transformHexToHex(hexValue, -0.3),
      shade_40: this.transformHexToHex(hexValue, -0.4),
      shade_50: this.transformHexToHex(hexValue, -0.5),
      shade_60: this.transformHexToHex(hexValue, -0.6),
      shade_70: this.transformHexToHex(hexValue, -0.7),
      shade_80: this.transformHexToHex(hexValue, -0.8),
      shade_90: this.transformHexToHex(hexValue, -0.9)
    };
  }

  static getAllShadesOfRgb(rgbValue: Array<number>): IShades {
    return {
      shade_10: this.transformRgbToRgb(rgbValue, -0.1),
      shade_20: this.transformRgbToRgb(rgbValue, -0.2),
      shade_30: this.transformRgbToRgb(rgbValue, -0.3),
      shade_40: this.transformRgbToRgb(rgbValue, -0.4),
      shade_50: this.transformRgbToRgb(rgbValue, -0.5),
      shade_60: this.transformRgbToRgb(rgbValue, -0.6),
      shade_70: this.transformRgbToRgb(rgbValue, -0.7),
      shade_80: this.transformRgbToRgb(rgbValue, -0.8),
      shade_90: this.transformRgbToRgb(rgbValue, -0.9)
    };
  }
}



// WEBPACK FOOTER //
// ./app/vendor/utility-ts-type/src/types/color.ts